import
{
  ControlValueAccessor,
  NG_VALUE_ACCESSOR,
  NG_VALIDATORS,
  ValidationErrors,
  Validator
} from '@angular/forms';
import { QueryOperatorDirective } from './query-operator.directive';
import { QueryFieldDirective } from './query-field.directive';
import { QueryEntityDirective } from './query-entity.directive';
import { QuerySwitchGroupDirective } from './query-switch-group.directive';
import { QueryButtonGroupDirective } from './query-button-group.directive';
import { QueryInputDirective } from './query-input.directive';
import { QueryRemoveButtonDirective } from './query-remove-button.directive';
import { sortBy } from 'lodash';
import
{
  ButtonGroupContext,
  Field,
  SwitchGroupContext,
  EntityContext,
  FieldContext,
  InputContext,
  LocalRuleMeta,
  OperatorContext,
  Option,
  QueryBuilderClassNames,
  QueryBuilderConfig,
  RemoveButtonContext,
  Rule,
  RuleSet,
} from './query-builder.interfaces';
import
{
  ChangeDetectorRef,
  Component,
  ContentChild,
  ContentChildren,
  forwardRef,
  Input,
  OnChanges,
  QueryList,
  TemplateRef,
  ChangeDetectionStrategy,
} from '@angular/core';
import { Entity } from './index';
import { QueryBuilderLocalizationService } from './query-builder-localization.service';
import { NGXLogger } from 'ngx-logger';

export const CONTROL_VALUE_ACCESSOR: any = {
  provide: NG_VALUE_ACCESSOR,
  useExisting: forwardRef(() => QueryBuilderComponent),
  multi: true
};

export const VALIDATOR: any = {
  provide: NG_VALIDATORS,
  useExisting: forwardRef(() => QueryBuilderComponent),
  multi: true
};
@Component({
  changeDetection: ChangeDetectionStrategy.Default,
  selector: 'app-query-builder',
  templateUrl: './query-builder.component.html',
  styleUrls: ['./query-builder.component.scss'],
  providers: [CONTROL_VALUE_ACCESSOR, VALIDATOR]
})
export class QueryBuilderComponent implements OnChanges, ControlValueAccessor, Validator
{
  @Input() public disabled: boolean;
  @Input() public data: RuleSet = { condition: 'and', rules: [] };

  @Input() public allowRuleset = true;
  @Input() public classNames: QueryBuilderClassNames;
  @Input() public operatorMap: { [key: string]: string[] };
  @Input() public parentValue: RuleSet;
  @Input() public config: QueryBuilderConfig = { fields: {} };
  @Input() public parentInputTemplates: QueryList<QueryInputDirective>;
  @Input() public parentOperatorTemplate: QueryOperatorDirective;
  @Input() public parentFieldTemplate: QueryFieldDirective;
  @Input() public parentEntityTemplate: QueryEntityDirective;
  @Input() public parentSwitchGroupTemplate: QuerySwitchGroupDirective;
  @Input() public parentButtonGroupTemplate: QueryButtonGroupDirective;
  @Input() public parentRemoveButtonTemplate: QueryRemoveButtonDirective;
  @Input() public parentChangeCallback: () => void;
  @Input() public parentTouchedCallback: () => void;

  @ContentChild(QueryButtonGroupDirective) public buttonGroupTemplate: QueryButtonGroupDirective;
  @ContentChild(QuerySwitchGroupDirective) public switchGroupTemplate: QuerySwitchGroupDirective;
  @ContentChild(QueryFieldDirective) public fieldTemplate: QueryFieldDirective;
  @ContentChild(QueryEntityDirective) public entityTemplate: QueryEntityDirective;
  @ContentChild(QueryOperatorDirective) public operatorTemplate: QueryOperatorDirective;
  @ContentChild(QueryRemoveButtonDirective) public removeButtonTemplate: QueryRemoveButtonDirective;
  @ContentChildren(QueryInputDirective) public inputTemplates: QueryList<QueryInputDirective>;

  public selectTruncateSize = 100;
  public fields: Field[];
  public filterFields: Field[];
  public entities: Entity[];
  public defaultClassNames: QueryBuilderClassNames = {
    removeIcon: 'q-icon q-remove-icon',
    addIcon: 'q-icon q-add-icon',
    button: 'q-button',
    buttonGroup: 'q-button-group',
    removeButton: 'q-remove-button',
    switchGroup: 'q-switch-group',
    switchLabel: 'q-switch-label',
    switchRadio: 'q-switch-radio',
    rightAlign: 'q-right-align',
    transition: 'q-transition',
    tree: 'q-tree',
    row: 'q-row',
    connector: 'q-connector',
    rule: 'q-rule',
    ruleSet: 'q-ruleset',
    invalidRuleSet: 'q-invalid-ruleset',
    emptyWarning: 'q-empty-warning',
    fieldControl: 'q-field-control',
    fieldControlSize: 'q-control-size',
    entityControl: 'q-entity-control',
    entityControlSize: 'q-control-size',
    operatorControl: 'q-operator-control',
    operatorControlSize: 'q-control-size',
    inputControl: 'q-input-control',
    inputControlSize: 'q-control-size'
  };

  public defaultOperatorMap: { [key: string]: string[] } = {
    String: ['=', '!=', 'contains', 'not contains'],
    Number: ['=', '!=', '>', '>=', '<', '<='],
    Time: ['=', '!=', '>', '>=', '<', '<='],
    Date: ['=', '!=', '>', '>=', '<', '<='],
    Category: ['=', '!=', 'in', 'not in'],
    Boolean: ['='],
    DateTime: ['=', '!=', '>', '>=', '<', '<='],
  };

  // For ControlValueAccessor interface
  public isUpdating = false;
  public onChangeCallback: () => void;
  public onTouchedCallback: () => any;

  private defaultTemplateTypes: string[] = [
    'String', 'Number', 'Time', 'Date', 'Category', 'Boolean', 'Multiselect', 'DateTime'];
  private defaultEmptyList: any[] = [];
  private operatorsCache: { [key: string]: string[] };
  private inputContextCache = new Map<Rule, InputContext>();
  private operatorContextCache = new Map<Rule, OperatorContext>();
  private fieldContextCache = new Map<Rule, FieldContext>();
  private entityContextCache = new Map<Rule, EntityContext>();
  private removeButtonContextCache = new Map<Rule, RemoveButtonContext>();
  private buttonGroupContext: ButtonGroupContext;

  constructor(
    private changeDetectorRef: ChangeDetectorRef,
    private logger: NGXLogger,
    private localService: QueryBuilderLocalizationService) { }

  @Input() public get value(): RuleSet
  {
    return this.data;
  }

  public set value(value: RuleSet)
  {
    // When component is initialized without a formControl, null is passed to value
    this.data = value || { condition: 'and', rules: [] };
    this.handleDataChange();
  }

  // ----------OnChanges Implementation----------

  public ngOnChanges(): void
  {
    const config = this.config;
    const type = typeof config;
    if (type === 'object')
    {
      this.fields = Object.keys(config.fields).map((value) =>
      {
        const field = config.fields[value];
        field.value = field.value || value;
        return field;
      });
      if (config.entities)
      {
        this.entities = Object.keys(config.entities).map((value) =>
        {
          const entity = config.entities[value];
          entity.value = entity.value || value;
          return entity;
        });
      }
      else
      {
        this.entities = null;
      }
      this.operatorsCache = {};
    }
    else
    {
      throw new Error(`Expected 'config' must be a valid object, got ${type} instead.`);
    }
  }

  // ----------Validator Implementation----------
  public validate(): ValidationErrors | null
  {
    const errors: { [key: string]: any } = {};
    const ruleErrorStore = [];
    let hasErrors = false;

    if (!this.config.allowEmptyRulesets && this.checkEmptyRuleInRuleset(this.data))
    {
      errors.empty = 'Empty rulesets are not allowed.';
      hasErrors = true;
    }

    this.validateRulesInRuleset(this.data, ruleErrorStore);

    if (ruleErrorStore.length)
    {
      errors.rules = ruleErrorStore;
      hasErrors = true;
    }
    return hasErrors ? errors : null;
  }

  // ----------ControlValueAccessor Implementation----------

  public writeValue(obj: any): void
  {
    this.value = obj;
  }

  public registerOnChange(fn: any): void
  {
    this.onChangeCallback = () => fn(this.data);
  }

  public registerOnTouched(fn: any): void
  {
    this.onTouchedCallback = () => fn(this.data);
  }

  public setDisabledState(isDisabled: boolean): void
  {
    this.disabled = isDisabled;
    this.changeDetectorRef.detectChanges();
  }

  // ----------END----------

  /**
   * Custom code to return the operator text to display. This is the less intrusive method.
   */
  public getOperatorText = (operator: string): string => this.localService.getOperatorText(operator);

  public getDisabledState = (): boolean => this.disabled;

  public findTemplateForRule(rule: Rule): TemplateRef<any>
  {
    const type = this.getInputType(rule.field, rule.operator);
    if (type)
    {
      const queryInput = this.findQueryInput(type);
      if (queryInput)
      {
        return queryInput.template;
      }
      else
      {
        if (this.defaultTemplateTypes.indexOf(type) === -1)
        {
          this.logger.warn(`Could not find template for field with type: ${type}`);
        }
        return null;
      }
    }
  }

  public findQueryInput(type: string): QueryInputDirective
  {
    const templates = this.parentInputTemplates || this.inputTemplates;
    return templates.find((item) => item.queryInputType === type);
  }

  public getOperators(field: string): string[]
  {
    if (this.operatorsCache[field])
    {
      return this.operatorsCache[field];
    }
    let operators = this.defaultEmptyList;
    const fieldObject = this.config.fields[field];

    if (this.config.getOperators)
    {
      return this.config.getOperators(field, fieldObject);
    }

    const type = fieldObject.type;

    if (fieldObject && fieldObject.operators)
    {
      operators = fieldObject.operators;
    }
    else if (type)
    {
      operators = (this.operatorMap && this.operatorMap[type]) || this.defaultOperatorMap[type] || this.defaultEmptyList;
      if (operators.length === 0)
      {
        this.logger.warn(
          `No operators found for field '${field}' with type ${fieldObject.type}. ` +
          `Please define an 'operators' property on the field or use the 'operatorMap' binding to fix this.`);
      }
      if (fieldObject.nullable)
      {
        operators = operators.concat(['is null', 'is not null']);
      }
    }
    else
    {
      this.logger.warn(`No 'type' property found on field: '${field}'`);
    }

    // Cache reference to array object, so it won't be computed next time and trigger a rerender.
    this.operatorsCache[field] = operators;
    return operators;
  }

  public getFields(entity: string): Field[]
  {
    if (this.entities && entity)
    {
      return this.fields.filter((field) => field && field.entity === entity);
    }
    else
    {
      return sortBy(this.fields, ['name']);
    }
  }

  public getInputType(field: string, operator: string): string
  {
    if (this.config.getInputType)
    {
      return this.config.getInputType(field, operator);
    }
    const type = this.config.fields[field].type;
    switch (operator)
    {
      case 'is null':
      case 'is not null':
        return null;  // No displayed component
      case 'in':
      case 'not in':
        return type === 'Category' || type === 'Boolean' ? 'Multiselect' : type;
      default:
        return type;
    }
  }

  public getOptions(field: string): Option[]
  {
    if (this.config.getOptions)
    {
      return this.config.getOptions(field);
    }
    return this.config.fields[field].options || this.defaultEmptyList;
  }

  public getClassNames(...args): string
  {
    const clsLookup = this.classNames ? this.classNames : this.defaultClassNames;
    const classNames = args.map((id) => clsLookup[id] || this.defaultClassNames[id]).filter((c) => !!c);
    return classNames.length ? classNames.join(' ') : null;
  }

  public getDefaultField(entity: Entity): Field
  {
    if (!entity)
    {
      return null;
    }
    else if (entity.defaultField !== undefined)
    {
      return this.getDefaultValue(entity.defaultField);
    }
    else
    {
      const entityFields = this.fields.filter((field) => field && field.entity === entity.value);
      if (entityFields && entityFields.length)
      {
        return entityFields[0];
      }
      else
      {
        this.logger.warn(`No fields found for entity '${entity.name}'. ` +
          `A 'defaultOperator' is also not specified on the field config. Operator value will default to null.`);
        return null;
      }
    }
  }

  public getDefaultOperator(field: Field): string
  {
    if (field && field.defaultOperator !== undefined)
    {
      return this.getDefaultValue(field.defaultOperator);
    }
    else
    {
      const operators = this.getOperators(field.value);
      if (operators && operators.length)
      {
        return operators[0];
      }
      else
      {
        this.logger.warn(`No operators found for field '${field.value}'. ` +
          `A 'defaultOperator' is also not specified on the field config. Operator value will default to null.`);
        return null;
      }
    }
  }

  public addRule(parent?: RuleSet): void
  {
    if (this.disabled)
    {
      return;
    }

    parent = parent || this.data;
    if (this.config.addRule)
    {
      this.config.addRule(parent);
    }
    else
    {
      const field = this.fields[0];
      parent.rules = parent.rules.concat([{
        field: field.value,
        operator: this.getDefaultOperator(field),
        value: this.getDefaultValue(field.defaultValue),
        entity: field.entity
      }]);
    }

    this.handleTouched();
    this.handleDataChange();
  }

  public removeRule(rule: Rule, parent?: RuleSet): void
  {
    if (this.disabled)
    {
      return;
    }

    parent = parent || this.data;
    if (this.config.removeRule)
    {
      this.config.removeRule(rule, parent);
    }
    else
    {
      parent.rules = parent.rules.filter((r) => r !== rule);
    }
    this.inputContextCache.delete(rule);
    this.operatorContextCache.delete(rule);
    this.fieldContextCache.delete(rule);
    this.entityContextCache.delete(rule);
    this.removeButtonContextCache.delete(rule);

    this.handleTouched();
    this.handleDataChange();

    if (parent.rules && parent.rules.length === 0) { this.removeRuleSet(undefined); }  /*RC: Automatically remove empty ruleset*/
  }

  public addRuleSet(parent?: RuleSet): void
  {
    if (this.disabled)
    {
      return;
    }

    parent = parent || this.data;
    if (this.config.addRuleSet)
    {
      this.config.addRuleSet(parent);
    }
    else
    {
      /*RC: Original
      parent.rules = parent.rules.concat([{condition: 'and', rules: []}]);*/
      /*RC: Automatically add default Rule*/
      const newRule = [];
      const field = this.fields[0];
      newRule.push({
        field: field.value,
        operator: this.getDefaultOperator(field),
        value: this.getDefaultValue(field.defaultValue),
        entity: field.entity
      });
      parent.rules = parent.rules.concat([{ condition: 'or', rules: newRule }]);
    }

    this.handleTouched();
    this.handleDataChange();
  }

  public removeRuleSet(ruleset?: RuleSet, parent?: RuleSet): void
  {
    if (this.disabled)
    {
      return;
    }

    ruleset = ruleset || this.data;
    parent = parent || this.parentValue;
    if (this.config.removeRuleSet)
    {
      this.config.removeRuleSet(ruleset, parent);
    }
    else
    {
      if (parent && parent.rules)
      {     // RC: Prevent error if last to remove
        parent.rules = parent.rules.filter((r) => r !== ruleset);
      }
    }

    this.handleTouched();
    this.handleDataChange();
  }

  public changeCondition(value: string): void
  {
    if (this.disabled)
    {
      return;
    }

    this.data.condition = value;
    this.handleTouched();
    this.handleDataChange();
  }

  public changeOperator(rule: Rule): void
  {
    if (this.disabled)
    {
      return;
    }

    if (this.config.coerceValueForOperator)
    {
      rule.value = this.config.coerceValueForOperator(rule.operator, rule.value, rule);
    }
    else
    {
      rule.value = this.coerceValueForOperator(rule.operator, rule.value, rule);
    }

    this.handleTouched();
    this.handleDataChange();
  }

  public coerceValueForOperator(operator: string, value: any, rule: Rule): any
  {
    const inputType: string = this.getInputType(rule.field, operator);
    if (inputType === 'Multiselect' && !Array.isArray(value))
    {
      return [value];
    }
    return value;
  }

  public changeInput(): void
  {
    if (this.disabled)
    {
      return;
    }

    this.handleTouched();
    this.handleDataChange();
  }

  /**
   * Get the field title.
   *
   * @param fieldValue Current selected field.
   */
  public getFieldTitle(fieldValue: string): string
  {
    const field: Field = this.config.fields[fieldValue];
    if (field)
    {
      return field.name;
    }
    return '';
  }

  public changeField(fieldValue: string, rule: Rule): void
  {
    if (this.disabled)
    {
      return;
    }

    const field: Field = this.config.fields[fieldValue];

    if (field && field.defaultValue !== undefined)
    {
      rule.value = this.getDefaultValue(field.defaultValue);
    }
    else
    {
      delete rule.value;
    }

    rule.operator = this.getDefaultOperator(field);

    // Create new context objects so templates will automatically update
    try
    {
      this.isUpdating = true;   // Set isUpdating to true to make sure the value select is refreshing properly

      this.inputContextCache.delete(rule);
      this.operatorContextCache.delete(rule);
      this.fieldContextCache.delete(rule);
      this.entityContextCache.delete(rule);
      this.getInputContext(rule);
      this.getFieldContext(rule);
      this.getOperatorContext(rule);
      this.getEntityContext(rule);

      this.handleTouched();
      this.handleDataChange();
      this.changeDetectorRef.detectChanges();
    }
    finally
    {
      this.isUpdating = false;
    }
  }

  public changeEntity(entityValue: string, rule: Rule, index: number, data: RuleSet): void
  {
    if (this.disabled)
    {
      return;
    }

    const entity: Entity = this.entities.find(e => e.value === entityValue);
    const defaultField: Field = this.getDefaultField(entity);
    data.rules[index] = {
      ...rule,
      field: defaultField.value
    };
    if (defaultField)
    {
      this.changeField(defaultField.value, {
        ...rule,
        field: defaultField.value
      });
    }
    else
    {
      this.handleTouched();
      this.handleDataChange();
    }
  }

  public getDefaultValue(defaultValue: any): any
  {
    switch (typeof defaultValue)
    {
      case 'function':
        return defaultValue();
      default:
        return defaultValue;
    }
  }

  public getOperatorTemplate(): TemplateRef<any>
  {
    const t = this.parentOperatorTemplate || this.operatorTemplate;
    return t ? t.template : null;
  }

  public getFieldTemplate(): TemplateRef<any>
  {
    const t = this.parentFieldTemplate || this.fieldTemplate;
    return t ? t.template : null;
  }

  public getEntityTemplate(): TemplateRef<any>
  {
    const t = this.parentEntityTemplate || this.entityTemplate;
    return t ? t.template : null;
  }

  public getButtonGroupTemplate(): TemplateRef<any>
  {
    const t = this.parentButtonGroupTemplate || this.buttonGroupTemplate;
    return t ? t.template : null;
  }

  public getSwitchGroupTemplate(): TemplateRef<any>
  {
    const t = this.parentSwitchGroupTemplate || this.switchGroupTemplate;
    return t ? t.template : null;
  }

  public getRemoveButtonTemplate(): TemplateRef<any>
  {
    const t = this.parentRemoveButtonTemplate || this.removeButtonTemplate;
    return t ? t.template : null;
  }

  public getQueryItemClassName(local: LocalRuleMeta): string
  {
    let cls = this.getClassNames('row', 'connector', 'transition');
    cls += ' ' + this.getClassNames(local.ruleset ? 'ruleSet' : 'rule');
    if (local.invalid)
    {
      cls += ' ' + this.getClassNames('invalidRuleSet');
    }
    return cls;
  }

  public getButtonGroupContext(): ButtonGroupContext
  {
    if (!this.buttonGroupContext)
    {
      this.buttonGroupContext = {
        addRule: this.addRule.bind(this),
        addRuleSet: this.allowRuleset && this.addRuleSet.bind(this),
        removeRuleSet: this.allowRuleset && this.parentValue && this.removeRuleSet.bind(this),
        getDisabledState: this.getDisabledState,
        $implicit: this.data
      };
    }
    return this.buttonGroupContext;
  }

  public getRemoveButtonContext(rule: Rule): RemoveButtonContext
  {
    if (!this.removeButtonContextCache.has(rule))
    {
      this.removeButtonContextCache.set(rule, {
        removeRule: this.removeRule.bind(this),
        getDisabledState: this.getDisabledState,
        $implicit: rule
      });
    }
    return this.removeButtonContextCache.get(rule);
  }

  public getFieldContext(rule: Rule): FieldContext
  {
    if (!this.fieldContextCache.has(rule))
    {
      this.fieldContextCache.set(rule, {
        onChange: this.changeField.bind(this),
        getFields: this.getFields.bind(this),
        getDisabledState: this.getDisabledState,
        fields: this.fields,
        $implicit: rule
      });
    }
    return this.fieldContextCache.get(rule);
  }

  public getEntityContext(rule: Rule): EntityContext
  {
    if (!this.entityContextCache.has(rule))
    {
      this.entityContextCache.set(rule, {
        onChange: this.changeEntity.bind(this),
        getDisabledState: this.getDisabledState,
        entities: this.entities,
        $implicit: rule
      });
    }
    return this.entityContextCache.get(rule);
  }

  public getSwitchGroupContext(): SwitchGroupContext
  {
    return {
      onChange: this.changeCondition.bind(this),
      getDisabledState: this.getDisabledState,
      $implicit: this.data
    };
  }

  public getOperatorContext(rule: Rule): OperatorContext
  {
    if (!this.operatorContextCache.has(rule))
    {
      this.operatorContextCache.set(rule, {
        onChange: this.changeOperator.bind(this),
        getDisabledState: this.getDisabledState,
        operators: this.getOperators(rule.field),
        $implicit: rule
      });
    }
    return this.operatorContextCache.get(rule);
  }

  public getInputContext(rule: Rule): InputContext
  {
    if (!this.inputContextCache.has(rule))
    {
      this.inputContextCache.set(rule, {
        onChange: this.changeInput.bind(this),
        getDisabledState: this.getDisabledState,
        options: this.getOptions(rule.field),
        field: this.config.fields[rule.field],
        $implicit: rule
      });
    }
    return this.inputContextCache.get(rule);
  }

  private checkEmptyRuleInRuleset(ruleset: RuleSet): boolean
  {
    if (!ruleset || !ruleset.rules || ruleset.rules.length === 0)
    {
      return true;
    }
    else
    {
      return ruleset.rules.some((item: RuleSet) =>
      {
        if (item.rules)
        {
          return this.checkEmptyRuleInRuleset(item);
        }
        else
        {
          return false;
        }
      });
    }
  }

  private validateRulesInRuleset(ruleset: RuleSet, errorStore: any[])
  {
    if (ruleset && ruleset.rules && ruleset.rules.length > 0)
    {
      ruleset.rules.forEach((item) =>
      {
        if ((item as RuleSet).rules)
        {
          return this.validateRulesInRuleset(item as RuleSet, errorStore);
        }
        else if ((item as Rule).field)
        {
          const field = this.config.fields[(item as Rule).field];
          if (field && field.validator && field.validator.apply)
          {
            const error = field.validator(item as Rule, ruleset);
            if (error != null)
            {
              errorStore.push(error);
            }
          }
        }
      });
    }
  }

  private handleDataChange(): void
  {
    this.changeDetectorRef.markForCheck();
    if (this.onChangeCallback)
    {
      this.onChangeCallback();
    }
    if (this.parentChangeCallback)
    {
      this.parentChangeCallback();
    }
  }

  private handleTouched(): void
  {
    if (this.onTouchedCallback)
    {
      this.onTouchedCallback();
    }
    if (this.parentTouchedCallback)
    {
      this.parentTouchedCallback();
    }
  }
}
